/**
 * @file Ввод пароля шифрования хранилища.
 *
 * Сначала проверяет, нужен ли пароль для выбранной папки, затем выполняет
 * challenge-проверку. Это не форма пароля учётной записи.
 */
import { useEffect, useId, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { FiEye, FiEyeOff, FiLock } from 'react-icons/fi'

import { useLocale } from '../../i18n/useLocale'
import './PasswordPrompt.css'
import { passwordRequired, unlockErrorCode, unlockStorage } from './api'
import type { UnlockErrorCode } from './api'
import { unlockMessages } from './messages'

export default function PasswordPrompt({
    folderPath,
    boxID,
    token,
    onSubmit,
}: {
    folderPath: string
    boxID: string
    token: string
    onSubmit: (password: string) => void
}) {
    const { locale } = useLocale()
    const text = unlockMessages[locale]

    // Содержимое поля и видимость пароля относятся к форме, а не к сессии входа.
    const id = useId()
    const [value, setValue] = useState('')
    const [visible, setVisible] = useState(false)

    // Сначала выясняем, нужен ли пароль, и лишь затем разрешаем его отправку.
    const [availability, setAvailability] = useState<
        'checking' | 'required' | 'error'
    >('checking')
    const [error, setError] = useState<UnlockErrorCode | null>(null)
    const [pending, setPending] = useState(false)
    const [attempt, setAttempt] = useState(0)

    // Запрос и поле доступны обработчикам без хранения DOM/контроллера в state.
    const request = useRef<AbortController | null>(null)
    const input = useRef<HTMLInputElement>(null)

    useEffect(() => {
        const controller = new AbortController()
        request.current?.abort()
        request.current = null
        setPending(false)
        setAvailability('checking')
        setError(null)
        setValue('')
        setVisible(false)
        passwordRequired({ folderPath, boxID, token }, controller.signal)
            .then((required) => {
                if (controller.signal.aborted) return

                if (required) setAvailability('required')
                else onSubmit('')
            })
            .catch((cause) => {
                if (controller.signal.aborted) return

                setAvailability('error')
                setError(unlockErrorCode(cause))
            })

        return () => {
            controller.abort()
            request.current?.abort()
        }
    }, [folderPath, boxID, token, onSubmit, attempt])

    useEffect(() => {
        if (availability === 'required' && !pending && error)
            input.current?.focus()
    }, [availability, pending, error])

    async function submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault()
        if (request.current) return

        if (!value) {
            setError('empty')
            input.current?.focus()

            return
        }

        const controller = new AbortController()
        request.current = controller
        setPending(true)
        setError(null)

        try {
            await unlockStorage(
                { folderPath, boxID, token },
                value,
                controller.signal,
            )
            if (!controller.signal.aborted) {
                setValue('')
                onSubmit(value)
            }
        } catch (cause) {
            if (!controller.signal.aborted) setError(unlockErrorCode(cause))
        } finally {
            if (request.current === controller) request.current = null

            if (!controller.signal.aborted) setPending(false)
        }
    }

    return (
        <section className='unlock-page' aria-labelledby={`${id}-title`}>
            <div className='unlock-card'>
                <div className='unlock-icon' aria-hidden='true'>
                    <FiLock />
                </div>

                <h1 id={`${id}-title`}>{text.title}</h1>

                <p className='unlock-description' id={`${id}-description`}>
                    {text.description}
                </p>

                <p className='unlock-folder'>
                    {text.folder}: <span>{folderPath}</span>
                </p>

                {availability === 'checking' && (
                    <p className='unlock-status' role='status'>
                        {text.checking}
                    </p>
                )}
                {availability === 'error' && (
                    <>
                        <p className='unlock-error' role='alert'>
                            {error && text.errors[error]}
                        </p>

                        <button
                            type='button'
                            className='unlock-submit'
                            onClick={() =>
                                setAttempt((previous) => previous + 1)
                            }
                        >
                            {text.retry}
                        </button>
                    </>
                )}
                {availability === 'required' && (
                    <form onSubmit={submit} noValidate aria-busy={pending}>
                        <label htmlFor={`${id}-password`}>{text.label}</label>
                        <div className='unlock-password'>
                            <input
                                ref={input}
                                id={`${id}-password`}
                                name='storage-password'
                                type={visible ? 'text' : 'password'}
                                autoComplete='off'
                                autoCapitalize='none'
                                spellCheck={false}
                                autoFocus
                                value={value}
                                disabled={pending}
                                aria-invalid={
                                    error === 'empty' || error === 'rejected'
                                }
                                aria-describedby={`${id}-description${error ? ` ${id}-error` : ''}`}
                                onChange={(event) => {
                                    setValue(event.target.value)
                                    setError(null)
                                }}
                            />

                            <button
                                type='button'
                                className='unlock-visibility'
                                disabled={pending}
                                aria-label={visible ? text.hide : text.show}
                                aria-pressed={visible}
                                onClick={() =>
                                    setVisible((previous) => !previous)
                                }
                            >
                                {visible ? (
                                    <FiEyeOff aria-hidden='true' />
                                ) : (
                                    <FiEye aria-hidden='true' />
                                )}
                            </button>
                        </div>

                        {error && (
                            <p
                                className='unlock-error'
                                id={`${id}-error`}
                                role='alert'
                            >
                                {text.errors[error]}
                            </p>
                        )}

                        <button
                            type='submit'
                            className='unlock-submit'
                            disabled={pending}
                        >
                            {pending ? text.pending : text.submit}
                        </button>
                    </form>
                )}
            </div>
        </section>
    )
}
